import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useSurveyPaths } from '../../../hooks/useSurveyPaths';
import { Link } from 'react-router-dom';
import { Heading, Paragraph } from '@digdir/designsystemet-react';
import type { PlanArea } from '../../../api/workEnvironmentTypes';
import { SavedText } from '../components/FormParts';
import { areaName, oneDecimal } from '../riskView';
import process from '../WorkEnvironmentSurveyProcess.module.css';

type Props = {
  surveyId: number;
  area: PlanArea;
  /** A plan for several areas has one card per area, each named */
  named: boolean;
};

export const AreaInfoCard = ({ surveyId, area, named }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const paths = useSurveyPaths();
  return (
    <section aria-labelledby={`plan-area-info-${area.key}`} className={`${process.card} ${process.sideCard}`}>
      <Heading level={2} data-size="xs" id={`plan-area-info-${area.key}`}>
        {named ? areaName(area, t) : t('workEnvironmentPlan.info.title')}
      </Heading>
      <SavedText
        title={t('workEnvironmentPlan.info.riskTitle')}
        text={t('workEnvironmentPlan.info.risk', {
          level: t(`workEnvironmentRisk.matrix.levels.${area.level}`),
          consequence: area.consequence,
          likelihood: area.likelihood,
          score: area.score,
        })}
      />
      <SavedText
        title={t(`hms.dimensions.${area.dimension}`)}
        text={t('workEnvironmentRisk.inNumbers.outOf', { value: oneDecimal(area.dimensionScore, i18n.language) })}
      />
      <SavedText
        title={t('workEnvironmentPlan.info.team')}
        text={t('workEnvironmentPlan.choose.engagement', {
          count: area.proposals,
          highlights: t('workEnvironmentPlan.choose.highlights', { count: area.highlights }),
        })}
      />
      {area.weakestQuestion && (
        <Paragraph data-size="sm">
          {t('workEnvironmentPlan.info.weakest', {
            text: area.weakestQuestion.text,
            mean: oneDecimal(area.weakestQuestion.mean, i18n.language),
          })}
        </Paragraph>
      )}
      <Link to={paths.report(surveyId)} className="ds-link">
        {t('workEnvironmentReport.card.open')}
      </Link>
    </section>
  );
};
